import { useEffect, useMemo } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import type { ReadingStepComponentProps } from "./types";
import { parseInstructionHtml } from "../utils";
import { AudioCaptureRecorder } from "../../../../components/AudioCaptureRecorder";
import { InstructionAudioButton } from "../../../../components/InstructionAudioButton";

export function RecapStep({
  assessment,
  isLoading,
  onStepReadyChange,
  onSaveAudio,
}: ReadingStepComponentProps) {
  const renderedRecapInstructions = useMemo(
    () => parseInstructionHtml(assessment?.recapInstructions ?? ""),
    [assessment?.recapInstructions],
  );

  useEffect(() => {
    if (!assessment) {
      onStepReadyChange?.(true);
    }
  }, [assessment, onStepReadyChange]);

  if (isLoading) {
    return null;
  }

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={3}
      sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}
    >
      <Card
        sx={{
          flex: "60%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {assessment?.recapImage ? (
          <Box
            component="img"
            src={assessment.recapImage}
            alt="Reading recap instructions"
            onError={(event) => {
              const target = event.currentTarget as HTMLImageElement;
              target.style.display = "none";
            }}
            sx={{ height: "100%", width: "100%", objectFit: "contain" }}
          />
        ) : (
          <Typography color="text.secondary">
            No image URL available.
          </Typography>
        )}
      </Card>
      <Card
        sx={{
          flex: "40%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
        variant={"filled"}
      >
        <CardContent sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Stack spacing={2} alignItems="center">
            <Stack
              direction={"column"}
              spacing={3}
              sx={{ width: "100%", minWidth: 0 }}
            >
              <Stack direction={"row"} alignItems={"center"} spacing={2}>
                <InstructionAudioButton
                  audioUrl={assessment?.recapInstructionsAudio}
                  label={`Play reading recap instruction audio`}
                ></InstructionAudioButton>
                <Typography variant={"body1"} sx={{ fontWeight: 800 }}>
                  Prompt:
                </Typography>
              </Stack>
              {renderedRecapInstructions ? (
                <Typography
                  component="div"
                  sx={{
                    width: "100%",
                    whiteSpace: "pre-line",
                    minWidth: 0,
                    fontWeight: 600,
                  }}
                >
                  {renderedRecapInstructions}
                </Typography>
              ) : (
                <Typography sx={{ width: "100%", minWidth: 0 }}>
                  Record your recap when you are ready.
                </Typography>
              )}
              <AudioCaptureRecorder
                onReadyChange={onStepReadyChange}
                onRecordingComplete={({ blob, mimeType, durationMs }) =>
                  onSaveAudio?.({
                    entityType: "recap_audio",
                    partOrder: assessment?.part?.partOrder ?? 1,
                    blob,
                    mimeType,
                    durationMs,
                  })
                }
                showTimer={false}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
